import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2, Printer, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { formatCurrency, formatDate, today, monthStart } from '../utils/helpers';

const empty = { bank_id:'', txn_date:today(), txn_type:'credit', amount:0, description:'', cheque_no:'', transaction_id:'', notes:'' };

export default function BankStatements() {
  const [banks, setBanks] = useState([]);
  const [txns, setTxns] = useState([]);
  const [totals, setTotals] = useState({ credits:0, debits:0, net:0 });
  const [bankId, setBankId] = useState('');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const params = new URLSearchParams();
    if (bankId) params.set('bank_id', bankId);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const r = await api.get('/bank-transactions?' + params.toString());
    setTxns(r.data.data || []);
    setTotals(r.data.totals || { credits:0, debits:0, net:0 });
  };

  useEffect(() => { api.get('/banks').then(r => setBanks(r.data.data || [])); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bankId, from, to]);

  const save = async () => {
    if (!form.bank_id) return toast.error('Select a bank');
    if (!form.amount || form.amount <= 0) return toast.error('Enter amount');
    try { await api.post('/bank-transactions', form); toast.success('Saved'); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this transaction? Bank balance will be reversed.')) return;
    try { await api.delete('/bank-transactions/' + id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const selectedBank = banks.find(b => b.id == bankId);

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card green">
          <div className="stat-label"><ArrowDownCircle size={12} style={{verticalAlign:'middle'}}/> Total Credits</div>
          <div className="stat-value">{formatCurrency(totals.credits)}</div>
        </div>
        <div className="stat-card red">
          <div className="stat-label"><ArrowUpCircle size={12} style={{verticalAlign:'middle'}}/> Total Debits</div>
          <div className="stat-value">{formatCurrency(totals.debits)}</div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Net Movement</div>
          <div className="stat-value" style={{color: totals.net >= 0 ? '#059669' : '#dc2626'}}>{formatCurrency(totals.net)}</div>
        </div>
        {selectedBank && (
          <div className="stat-card">
            <div className="stat-label">Current Balance</div>
            <div className="stat-value">{formatCurrency(selectedBank.current_balance)}</div>
            <div className="stat-sub">{selectedBank.bank_name}</div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Bank Statement ({txns.length} transactions)</h3>
          <div className="actions-group">
            <button className="btn btn-gray no-print" onClick={()=>window.print()}><Printer size={13}/> Print</button>
            <button className="btn btn-blue" onClick={() => { setForm({...empty, bank_id: bankId}); setOpen(true); }}><Plus size={14}/> Add Transaction</button>
          </div>
        </div>
        <div className="search-bar">
          <label style={{fontSize:12, fontWeight:600}}>Bank:</label>
          <select value={bankId} onChange={e=>setBankId(e.target.value)} style={{minWidth:200}}>
            <option value="">All Banks</option>
            {banks.map(b=><option key={b.id} value={b.id}>{b.bank_name} {b.account_no?`(${b.account_no})`:''}</option>)}
          </select>
          <label style={{fontSize:12, fontWeight:600}}>From:</label>
          <input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{width:160}}/>
          <label style={{fontSize:12, fontWeight:600}}>To:</label>
          <input type="date" value={to} onChange={e=>setTo(e.target.value)} style={{width:160}}/>
        </div>

        <div className="table-wrap">
          <table>
            <thead><tr><th>Txn No</th><th>Date</th><th>Bank</th><th>Type</th><th>Description</th><th>Ref</th><th>Cheque / TXN ID</th><th className="td-num">Amount</th><th className="td-num">Balance</th><th>Actions</th></tr></thead>
            <tbody>
              {txns.length ? txns.map(t => (
                <tr key={t.id}>
                  <td><strong>{t.txn_no}</strong></td>
                  <td>{formatDate(t.txn_date)}</td>
                  <td>{t.bank_name}</td>
                  <td>
                    <span className={'badge ' + (
                      t.txn_type==='credit'||t.txn_type==='transfer_in' ? 'badge-green' : 'badge-red'
                    )}>
                      {t.txn_type==='credit' ? 'CR' : t.txn_type==='debit' ? 'DR' : t.txn_type==='transfer_in' ? 'TRF IN' : 'TRF OUT'}
                    </span>
                  </td>
                  <td>{t.description || '-'}</td>
                  <td><small style={{color:'#64748b'}}>{t.reference_type || '-'}{t.related_bank_name ? ' · '+t.related_bank_name : ''}</small></td>
                  <td><small>{t.cheque_no || t.transaction_id || '-'}</small></td>
                  <td className={'td-num ' + (t.txn_type==='credit'||t.txn_type==='transfer_in' ? 'text-green' : 'text-red')}>
                    <strong>{t.txn_type==='credit'||t.txn_type==='transfer_in' ? '+' : '-'}{formatCurrency(t.amount)}</strong>
                  </td>
                  <td className="td-num">{formatCurrency(t.balance_after)}</td>
                  <td><button className="btn btn-sm btn-red no-print" onClick={()=>del(t.id)}><Trash2 size={12}/></button></td>
                </tr>
              )) : <tr><td colSpan="10" className="empty">No transactions in this period</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title="Add Bank Transaction"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>Save</button>
        </>}>
        <div className="form-grid">
          <div className="form-group"><label>Bank *</label>
            <select value={form.bank_id} onChange={e=>setForm({...form, bank_id:e.target.value})}>
              <option value="">-- Select --</option>
              {banks.map(b=><option key={b.id} value={b.id}>{b.bank_name}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Date *</label><input type="date" value={form.txn_date} onChange={e=>setForm({...form, txn_date:e.target.value})}/></div>
          <div className="form-group"><label>Type *</label>
            <select value={form.txn_type} onChange={e=>setForm({...form, txn_type:e.target.value})}>
              <option value="credit">Credit (Deposit / Income)</option>
              <option value="debit">Debit (Withdrawal / Payment)</option>
            </select>
          </div>
          <div className="form-group"><label>Amount *</label><input type="number" step="0.01" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})}/></div>
          <div className="form-group"><label>Cheque No</label><input value={form.cheque_no} onChange={e=>setForm({...form, cheque_no:e.target.value})}/></div>
          <div className="form-group"><label>Transaction ID</label><input value={form.transaction_id} onChange={e=>setForm({...form, transaction_id:e.target.value})}/></div>
          <div className="form-group full-width"><label>Description</label><input value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/></div>
          <div className="form-group full-width"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
